import {
  StoreNewerThanBundleError,
  StoreSupersededError,
  StoreUpgradeError,
} from '@jbroll/rowboat-client';
import { Component, type ReactNode } from 'react';
import { Button } from './ui';

interface Props {
  children: ReactNode;
  reload?: () => void;
}

/**
 * Shows why this device's store did not open, or stopped being usable. A failed open stays failed
 * for the life of the page, so nothing here retries. Any other error passes up.
 */
export class StoreErrorBoundary extends Component<Props, { error: unknown }> {
  override state: { error: unknown } = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  override render() {
    const { error } = this.state;
    if (error === null) return this.props.children;
    if (error instanceof StoreSupersededError) {
      const reload = this.props.reload ?? (() => location.reload());
      return (
        <div role="alert" className="flex flex-col items-center gap-3 p-6 text-center">
          RosaHouse was updated in another tab. Reload to keep working here.
          <Button onClick={reload}>Reload</Button>
        </div>
      );
    }
    if (error instanceof StoreNewerThanBundleError) {
      return (
        <p role="alert" className="p-6 text-center">
          The data on this device was saved by a newer version of RosaHouse than this one. Reload
          once the new version is ready.
        </p>
      );
    }
    if (error instanceof StoreUpgradeError) {
      return (
        <p role="alert" className="p-6 text-center">
          RosaHouse could not update the data saved on this device. Nothing was changed. (
          {error.message})
        </p>
      );
    }
    throw error;
  }
}
